import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../../Component/PageHeader";
import StatStrip from "../../Component/StatStrip";
import Pill from "../../Component/Pill";
import { CLIENTS, PLAN_MIX, REVENUE_12M, SIGNUP_FUNNEL, initials } from "../../utils/staticData";

// STATIC DESIGN — numbers come from utils/staticData until a dashboard API exists
const MONTHS = ["O", "N", "D", "J", "F", "M", "A", "M", "J", "J", "A", "S"];
const CHART_W = 640, CHART_H = 190, CHART_MAX = 300;

const ALERTS: [string, string, string, string, string, string][] = [
  // [tone, icon, title, detail, button, route]
  ["bad", "ri-bank-card-line", "Razorpay webhook failing", "3 renewals not applied · UrbanKart +2", "View", "/system-health"],
  ["bad", "ri-whatsapp-line", "2 numbers quality RED", "Metro Realty, BetWin Promotions", "Review", "/whatsapp-numbers"],
  ["warn", "ri-id-card-line", "2 affiliate KYC pending", "Oldest waiting 3 days", "Verify", "/affiliate-program/dashboard"],
  ["warn", "ri-global-line", "BizReply DNS not pointed", "portal.bizreply.com → CNAME missing", "Fix", "/white-label"],
  ["blue", "ri-customer-service-2-line", "1 urgent ticket unassigned", "TKT-3398 · 12 min", "Assign", "/tickets"],
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");
  const [chartView, setChartView] = useState("monthly");

  const totalWorkspaces = PLAN_MIX.reduce((s, p) => s + p[1], 0);
  const topClients = [...CLIENTS].sort((a, b) => b.msgs - a.msgs).slice(0, 4);

  return (
    <div className="container-fluid">
      <div className="cx">
        <PageHeader
          crumb="Dashboard"
          title="Dashboard"
          sub="Platform overview · last 30 days"
          actions={<>
            <select className="cx-select" style={{ height: 38 }} value={range} onChange={(e) => setRange(e.target.value)}>
              <option value="30d">Last 30 days</option>
              <option value="quarter">This quarter</option>
              <option value="year">This year</option>
            </select>
            <button type="button" className="cx-btn cx-btn-light" onClick={() => toast.success("Dashboard report exported")}>
              <i className="ri-download-2-line"></i>Export
            </button>
          </>}
        />

        <StatStrip items={[
          { label: "MRR", value: "₹27.4L", icon: "ri-money-rupee-circle-line", delta: 8.2 },
          { label: "Active workspaces", value: "2,816", icon: "ri-building-2-line", delta: 5.3 },
          { label: "Messages (30d)", value: "4.82 Cr", icon: "ri-chat-3-line", delta: 12.4 },
          { label: "Wallet float", value: "₹61.2L", icon: "ri-wallet-3-line", delta: -3.1 },
          { label: "Open alerts", value: "6", icon: "ri-alarm-warning-line", delta: "2 critical" },
        ]} />

        <div className="cx-grid g21">
          <div className="cx-card stretch">
            <div className="cx-card-h">
              <h6>Revenue <span className="sub">· subscriptions + wallet top-ups, ₹ lakh</span></h6>
              <div className="cx-seg">
                <button type="button" className={chartView === "monthly" ? "on" : ""} onClick={() => setChartView("monthly")}>Monthly</button>
                <button type="button" className={chartView === "weekly" ? "on" : ""} onClick={() => setChartView("weekly")}>Weekly</button>
              </div>
            </div>
            <div className="cx-card-b">
              <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} style={{ width: "100%", height: 190 }} role="img" aria-label="Revenue for the last 12 months">
                {[0, 1, 2, 3].map(i => {
                  const y = 10 + i * (CHART_H - 30) / 3;
                  return <line key={i} x1="0" x2={CHART_W} y1={y} y2={y} stroke="#eef0f3" />;
                })}
                {REVENUE_12M.map((v, i) => {
                  const bh = v / CHART_MAX * (CHART_H - 30);
                  const slot = CHART_W / 12;
                  return (
                    <g key={i}>
                      <rect x={i * slot + 10} y={CHART_H - 20 - bh} width={slot - 20} height={bh} rx="5" fill={i === 11 ? "#032e67" : "#88b3ed"}>
                        <title>{`₹${v / 10}L`}</title>
                      </rect>
                      <text x={i * slot + slot / 2} y={CHART_H - 4} textAnchor="middle" fontSize="10" fill="#849690" fontFamily="JetBrains Mono">{MONTHS[i]}</text>
                    </g>
                  );
                })}
              </svg>
              <div className="cx-legend">
                <span><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#032e67" }}></i>Current month ₹27.4L</span>
                <span><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#88b3ed" }}></i>Previous months</span>
                <span>Subscriptions 71% · Wallet 29%</span>
              </div>
            </div>
          </div>

          <div className="cx-card stretch">
            <div className="cx-card-h">
              <h6>Needs attention</h6>
              <Pill tone="bad">6 open</Pill>
            </div>
            <div className="cx-card-b">
              {ALERTS.map(([tone, icon, title, detail, btn, route]) => (
                <div className="cx-orow" key={title}>
                  <div className={`cx-ic ${tone}`}><i className={icon}></i></div>
                  <div>
                    <div className="t">{title}</div>
                    <div className="d" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{detail}</div>
                  </div>
                  <div className="right">
                    <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => navigate(route)}>{btn}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cx-grid g3 mt">
          <div className="cx-card stretch">
            <div className="cx-card-h">
              <h6>Plan mix</h6>
              <span className="sub mono">{totalWorkspaces.toLocaleString("en-IN")} workspaces</span>
            </div>
            <div className="cx-card-b">
              <div style={{ display: "flex", height: 9, borderRadius: 5, overflow: "hidden", marginBottom: 10 }}>
                {PLAN_MIX.map(([name, n, color]) => <i key={name} title={name} style={{ width: `${n / totalWorkspaces * 100}%`, background: color }}></i>)}
              </div>
              {PLAN_MIX.map(([name, n, color]) => (
                <div className="cx-mini" key={name}>
                  <span><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: color, marginRight: 8 }}></i>{name}</span>
                  <b className="mono">{n.toLocaleString("en-IN")}</b>
                </div>
              ))}
            </div>
          </div>

          <div className="cx-card stretch">
            <div className="cx-card-h"><h6>Signup funnel <span className="sub">· 30d</span></h6></div>
            <div className="cx-card-b">
              {SIGNUP_FUNNEL.map(([step, n, pct]) => (
                <div key={step} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                    <span>{step}</span>
                    <span className="mono">{n.toLocaleString("en-IN")} <span className="muted">· {pct}%</span></span>
                  </div>
                  <div className="cx-bar"><i style={{ width: `${pct}%` }}></i></div>
                </div>
              ))}
              <p className="cx-help">Biggest drop: <b>Email verified → WABA connected</b> (−31%).</p>
            </div>
          </div>

          <div className="cx-card stretch">
            <div className="cx-card-h">
              <h6>Top clients by usage</h6>
              <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => navigate("/customers-and-users")}>All</button>
            </div>
            <div className="cx-card-b">
              {topClients.map(c => (
                <div className="cx-orow click" key={c.id} onClick={() => navigate(`/customers-and-users?search=${encodeURIComponent(c.name)}`)}>
                  <div className="cx-who">
                    <div className="av">{initials(c.name)}</div>
                    <div><b>{c.name}</b><small>{c.plan}</small></div>
                  </div>
                  <div className="right mono fs12">{(c.msgs / 1000).toFixed(1)}K</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
